import { JobQueue, JobQueueService, JobQueueStatus, JobService, JobStatus, JobType } from "@app/jobs";
import { ProjectService } from "@app/projects";
import { QESButton } from "@app/shared/qes-standards";
import { formatJobsFlyoutAsOf, formatJobsFlyoutDuration } from "@app/shared/utility";
import { Waffles } from "@app/shared/waffles";
import { SortDirections } from "@app/shared/waffles/exports";
import { orderBy } from "lodash";
import * as React from "react";
import { QESSelect } from "../../shared/qes-standards";
import { RoleRestricted, Roles } from "../../user";
import { ProjectRunConfigurationJobStatus, ProjectRunConfigurationStatus } from "../enumerators";

/* eslint-disable sort-keys */
interface TableData {
    jobId: React.ReactNode;
    _jobId: number,                     //added for customSort only
    jobType: JobType;
    membership: string;
    stateName: string;
    county: string;
    jobStatus: React.ReactNode;
    _jobStatus: string;                  //added for customSort only
    asOf: string;
    _asOf: Date;                         //added for customSort only
    duration: string;
    queueName: string;
    queueDepthEstimate: string;
    key: string;
}

interface JobsPanelContentState {
    records: TableData[],
    queues: JobQueue[],
    lastRefresh: string,
}

interface JobsPanelContentProps {
    projectId: number;
    projectName: string;
    onclose?: () => void;
}

class JobsPanelContent extends React.PureComponent<JobsPanelContentProps, JobsPanelContentState> {
    private readonly jobService = new JobService();
    private readonly jobQueueService = new JobQueueService();
    private readonly projectService = new ProjectService();

    constructor(props: JobsPanelContentProps) {
        super(props);

        this.state = {
            records: [],
            queues: [],
            lastRefresh: "",
        };
    }

    handleCustomSort = (data: TableData[], column: string, sortDirection: SortDirections): TableData[] => {
        if (data === undefined)
            return;

        if (column === "jobId" || column === "jobStatus" || column === "asOf") {
            column = '_' + column;
        }

        const direction = sortDirection === SortDirections.Asc ? "asc" : "desc";
        return column !== "_asOf" ? orderBy(data, [column], [direction]) as TableData[]
            : orderBy(data, [d => new Date(d._asOf)], [direction]) as TableData[];

    }

    public render() {
        const columnsConfig = {
            jobId: {
                displayText: "Job ID",
                testId: "jobId",
            },
            jobType: {
                displayText: "Job Type",
                testId: "jobType"
            },
            membership: {
                displayText: "Membership",
                testId: "membership"
            },
            stateName: {
                displayText: "State",
                testId: "stateName",
            },
            county: {
                displayText: "County",
                testId: "county"
            },
            jobStatus: {
                displayText: "Job Status",
                testId: "jobStatus"
            },
            asOf: {
                displayText: "As Of",
                testId: "asOf"
            },
            duration: {
                displayText: "Duration",
                testId: "duration"
            },
            queueName: {
                displayText: "Queue Name",
                testId: "queueName"
            },
            queueDepthEstimate: {
                displayText: "Depth",
                testId: "queueDepthEstimate"
            }
        };

        return (
            <div className="row">
                <div className="mb-2 d-flex align-items-center justify-content-end col-12">
                    <div className="fs15 mr-3" >
                        Last Refreshed: {this.state.lastRefresh}
                    </div>
                    <QESButton
                        onClick={() => this.refreshData()}
                        qesTestId="job_ui_refresh_data"
                    >
                        Refresh Data
                    </QESButton>
                </div>
                <div className="col-12">
                    <Waffles<TableData>
                        columns={columnsConfig}
                        data={this.state.records}
                        fullWidth={true}
                        testId="job-status-table"
                        customSort={this.handleCustomSort}
                    />
                </div>
            </div>
        );
    }

    private async getJobStatuses() {
        const apiData = await this.jobService.getJobStatusesByCustomProjectIdAsync(this.props.projectId) as JobQueueStatus[];
        const records: TableData[] = [];

        apiData.forEach(data => {
            let heartbeatIcon: React.ReactNode;

            if (data.job.lastHeartbeatDate != undefined && data.job.jobStatus != "Finished" && data.job.jobStatus != "Canceled" && data.job.jobStatus != "Failed") {
                // checks if heartbeat received within last 15 minutes, which is 900,000 milliseconds
                if (Math.abs(new Date().getTime() - new Date(data.job.lastHeartbeatDate.toString()).getTime()) > 900000) {
                    heartbeatIcon = <i className="fa fa-exclamation-triangle" />;
                }
            }

            let jobStatusNode: React.ReactNode;

            // check all requirements to see if a job can be retried
            const isValidJobType = (data.job.jobType == JobType.CustomAdequacyRegionSummary || data.job.jobType == JobType.ProviderChangesRegionSummary);

            const runConfigStatus = data.projectRunConfigurationStatus;
            const isValidRunConfigStatus = (runConfigStatus != ProjectRunConfigurationStatus.Unknown
                && runConfigStatus != ProjectRunConfigurationStatus.Aggregating
                && runConfigStatus != ProjectRunConfigurationStatus.Succeeded
                && runConfigStatus != ProjectRunConfigurationStatus.Queued);

            const runConfigJobStatus = data.projectRunConfigurationJobStatus;
            const isValidProjectRunConfigJobStatus = (runConfigJobStatus != null
                && runConfigJobStatus != undefined
                && runConfigJobStatus != ProjectRunConfigurationJobStatus.Unknown
                && runConfigJobStatus != ProjectRunConfigurationJobStatus.Processing
                && runConfigJobStatus != ProjectRunConfigurationJobStatus.Loaded
                && runConfigJobStatus != ProjectRunConfigurationJobStatus.Purged
                && runConfigJobStatus != ProjectRunConfigurationJobStatus.Queued);

            const jobStatus = data.job.jobStatus;
            const isValidJobStatus = (jobStatus != null
                && jobStatus != JobStatus.Queued
                && jobStatus != JobStatus.Started
                && jobStatus != JobStatus.Finished
                && jobStatus != JobStatus.Created);

            let _jobStatus = "";
            if (isValidJobType && isValidRunConfigStatus && isValidProjectRunConfigJobStatus && isValidJobStatus) {
                jobStatusNode = <div>
                    {data.job.jobStatus}
                    <RoleRestricted allowedRoles={[Roles.OPERATIONS_USER]}>
                        <QESSelect
                            label=""
                            testId="jobs_panel_retry_job_select"
                            onChange={(e) => this.resubmitHandler(e, e.target.value, data.job.jobId, data.projectRunConfigurationId)}
                            defaultValue="0"
                        >
                            <option value="0">Select Retry Queue</option>
                            {this.state.queues.map((option) => (
                                <option key={option.id} value={option.name}>{`${option.name}`}</option>
                            ))}
                        </QESSelect>
                    </RoleRestricted>
                </div>
            }
            else {
                _jobStatus = data.job.jobStatus;
                jobStatusNode = <div>
                    {data.job.jobStatus}
                </div>
            }

            const seqLink: React.ReactNode = (
                <div>
                    <a href={data.jobUrl} target="_blank" rel="noreferrer">{data.job.jobId}</a>
                    {heartbeatIcon}
                </div>
            );

            const row: TableData = {
                _jobId: data.job.jobId,
                jobId: seqLink,
                jobType: data.job.jobType,
                membership: data.membership,
                stateName: (data.stateName) ? data.stateName : "",
                county: (data.county) ? data.county : "",
                jobStatus: jobStatusNode,
                _jobStatus: _jobStatus,
                asOf: formatJobsFlyoutAsOf(data.job.jobStatusDuration.statusAsOfDate),
                _asOf: data.job.jobStatusDuration.statusAsOfDate,
                duration: formatJobsFlyoutDuration(data.job.jobStatusDuration.duration),
                queueName: data.queueName,
                queueDepthEstimate: data.queueDepthEstimate.toString() === "0" ? "---" : `${data.queueDepthEstimate.toString()}/${data.jobCount}`,
                key: ""
            }

            records.push(row);
        });
        return records;
    }

    private resubmitHandler = async (e: React.ChangeEvent<HTMLSelectElement>, queueName: string, jobId: number, projectRunConfigurationId: number) => {
        e.target.disabled = true;

        await this.projectService.resubmitProjectJob(this.props.projectId, projectRunConfigurationId, jobId, queueName);

        await this.refreshData();
        e.target.disabled = false;
        e.target.selectedIndex = 1;
    }

    private setCurrentDateTime() {
        const d = new Date();
        const displayString = `${d.toLocaleDateString()} at ${d.toLocaleTimeString()}`;

        this.setState({
            lastRefresh: displayString
        })
    }

    private async refreshData() {
        let queues: JobQueue[];
        try {
          queues = await this.jobQueueService.getAvailableJobQueues();
        }
        catch (err) {
            queues = [];
        }
        this.setState({ queues });
        const records = await this.getJobStatuses();
        this.setState({ records });
        this.setCurrentDateTime();
    }

    public async componentDidMount() {
        await this.refreshData();
    }
}
/* eslint-enable sort-keys */

export default JobsPanelContent;